import React, { useState, useEffect, useRef } from 'react';
import { Flex, ClickableItem, Tooltip, Textarea, Button, Text } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { useAIChatDispatch, useAIChatSelector } from '../Context/AIChatContext';
import { submitFeedback } from '../Context/Helpers';
import BemClass from '@upsales/components/Utils/bemClass';
import { useSoftDeployAccess } from 'App/components/hooks';

import './FeedbackButtons.scss';

interface FeedbackButtonsProps {
	messageIndex: number;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ messageIndex }) => {
	const classes = new BemClass('FeedbackButtons');
	const { t } = useTranslation();
	const dispatch = useAIChatDispatch();
	const { feedbackGiven } = useAIChatSelector(({ feedbackGiven }) => ({ feedbackGiven }));
	const hasFeedbackComments = useSoftDeployAccess('AI_CHAT_FEEDBACK_COMMENTS');
	const [feedbackText, setFeedbackText] = useState('');
	const [selectedFeedbackType, setSelectedFeedbackType] = useState<'up' | 'down' | null>(null);
	const [showInput, setShowInput] = useState(false);
	const [showThankYou, setShowThankYou] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	const currentFeedback = feedbackGiven[messageIndex];
	const showAddCommentLink = hasFeedbackComments && selectedFeedbackType !== null && !showInput;

	const resetTimeout = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	const resetState = () => {
		resetTimeout();
		setShowInput(false);
		setFeedbackText('');
		setSelectedFeedbackType(null);
	};

	// Auto-submit feedback after 5 seconds if no comment is added (only if comments feature is enabled)
	useEffect(() => {
		if (hasFeedbackComments && showAddCommentLink && selectedFeedbackType) {
			timeoutRef.current = setTimeout(() => {
				dispatch(submitFeedback(messageIndex, selectedFeedbackType));
				resetState();
				// Show thank you message
				setShowThankYou(true);
			}, 5000);

			return resetTimeout;
		}
	}, [hasFeedbackComments, showAddCommentLink, selectedFeedbackType, messageIndex, dispatch]);

	// Scroll to textarea and focus textarea when it opens
	useEffect(() => {
		if (showInput && textareaRef.current) {
			setTimeout(() => {
				if (!textareaRef.current) return;

				// Find the scrollable parent container
				let scrollableParent: HTMLElement | null = textareaRef.current.parentElement;
				while (scrollableParent) {
					const style = window.getComputedStyle(scrollableParent);
					if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
						break;
					}
					scrollableParent = scrollableParent.parentElement;
				}

				if (scrollableParent) {
					const textareaRect = textareaRef.current.getBoundingClientRect();
					const parentRect = scrollableParent.getBoundingClientRect();
					const scrollTop = scrollableParent.scrollTop;
					const textareaTopRelative = textareaRect.top - parentRect.top + scrollTop;
					const padding = 100; // Extra padding to ensure textarea is visible

					scrollableParent.scrollTo({
						top: textareaTopRelative - padding,
						behavior: 'smooth'
					});
				} else {
					textareaRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					});
				}

				// Focus textarea after scrolling
				setTimeout(() => {
					if (textareaRef.current) {
						textareaRef.current.focus();
					}
				}, 300);
			}, 200);
		}
	}, [showInput]);

	const handleFeedbackClick = (feedbackType: 'up' | 'down') => {
		if (currentFeedback) return;
		resetTimeout();
		setSelectedFeedbackType(feedbackType);
		setShowInput(false);
		setFeedbackText('');
		
		// If comments feature is disabled, submit feedback immediately
		if (!hasFeedbackComments) {
			dispatch(submitFeedback(messageIndex, feedbackType));
			setSelectedFeedbackType(null);
			setShowThankYou(true);
		}
	};

	const handleSubmitFeedback = () => {
		if (selectedFeedbackType) {
			dispatch(submitFeedback(messageIndex, selectedFeedbackType, feedbackText.trim() || undefined));
			resetState();
			// Show thank you message
			setShowThankYou(true);
		}
	};

	return (
		<Flex direction="column" gap="u2" className={classes.mod({ disabled: !!currentFeedback }).b()}>
			<Flex justifyContent="flex-start" gap="u2" alignItems="center">
				<Tooltip disabled={!!currentFeedback} title={currentFeedback === 'up' ? t('default.thankyou') : t('aiChat.feedbackPositive')}>
					<ClickableItem
						icon="thumbs-up"
						size="sm"
						onClick={() => handleFeedbackClick('up')}
						disabled={!!currentFeedback}
						className={classes
							.elem('button')
							.mod({
								disabled: !!currentFeedback,
								active: currentFeedback === 'up' || selectedFeedbackType === 'up'
							})
							.b()}
					/>
				</Tooltip>
				<Tooltip disabled={!!currentFeedback} title={currentFeedback === 'down' ? t('default.thankyou') : t('aiChat.feedbackNegative')}>
					<ClickableItem
						icon="thumbs-down"
						size="sm"
						onClick={() => handleFeedbackClick('down')}
						disabled={!!currentFeedback}
						className={classes
							.elem('button')
							.mod({
								disabled: !!currentFeedback,
								active: currentFeedback === 'down' || selectedFeedbackType === 'down',
								down: true
							})
							.b()}
					/>
				</Tooltip>
				{showAddCommentLink && !currentFeedback && (
					<Text
						size="sm"
						color="purple"
						className={classes.elem('addCommentLink').b()}
						onClick={() => {
							resetTimeout();
							setShowInput(true);
						}}
					>
						{t('aiChat.addComment', 'Add comment?')}
					</Text>
				)}
				{showThankYou && (
					<Text size="sm" color="green">
						{t('default.thankyou', 'Thank you!')}
					</Text>
				)}
			</Flex>
			{showInput && !currentFeedback && (
				<Flex direction="column" gap="u2" className={classes.elem('feedbackInput').b()}>
					<Textarea
						textareaRef={r => (textareaRef.current = r)}
						placeholder={t('aiChat.feedbackPlaceholder', 'Berätta mer om din feedback...')}
						value={feedbackText}
						onChange={e => setFeedbackText(e.target.value)}
						onKeyDown={e => {
							if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
								e.preventDefault();
								if (selectedFeedbackType) {
									handleSubmitFeedback();
								}
							} else if (e.key === 'Escape') {
								e.preventDefault();
								resetState();
							}
						}}
						autoHeight
						className={classes.elem('textarea').b()}
					/>
					<Flex gap="u2" justifyContent="flex-end">
						<Button size="sm" type="lined" onClick={resetState}>
							{t('default.cancel')}
						</Button>
						<Button size="sm" onClick={handleSubmitFeedback} disabled={!selectedFeedbackType}>
							{t('default.send')}
						</Button>
					</Flex>
				</Flex>
			)}
		</Flex>
	);
};

export default FeedbackButtons;
